
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronRight, Wifi, WifiOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { FormFields } from './forms/FormFields';
import { SuccessDialog } from './forms/SuccessDialog';
import { sendToTelegram } from '@/utils/telegramService';
import { 
  RegistrationFormData, 
  registrationSchema, 
  validateCourseSpecificFields 
} from '@/utils/registrationSchema';
import { 
  saveRegistrationToDatabase, 
  sendRegistrationConfirmationEmail,
  RegistrationErrorType 
} from '@/utils/registrationService';

interface RegistrationFormProps {
  children: React.ReactNode;
  prefilledCourse?: string;
  prefilledFaculty?: string;
}

export const RegistrationForm = ({ children, prefilledCourse, prefilledFaculty }: RegistrationFormProps) => {

  const [formData, setFormData] = useState<RegistrationFormData>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    course: prefilledCourse || '',
    faculty: prefilledFaculty || '',
    specialization: '',
    language: '',
    learningType: prefilledCourse === 'ai-teacher-training' ? 'group' : '' as any,
    learningFormat: prefilledCourse === 'ai-teacher-training' ? 'inPerson' : '' as any,
    duration: prefilledCourse === 'ai-teacher-training' ? '3' : ''
  });
  
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const { toast } = useToast();

  // Network connectivity monitoring
  React.useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      console.log('🌐 Network connection restored');
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      console.log('📴 Network connection lost');
      toast({
        title: "Ցանցային կապի խնդիր",
        description: "Ինտերնետ կապը չակտիվ է",
        variant: "destructive"
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [toast]);

  // Real-time form validation
  const validateForm = (data: RegistrationFormData): string[] => {
    const errors: string[] = [];
    
    const validationResult = registrationSchema.safeParse(data);
    if (!validationResult.success) {
      errors.push(...validationResult.error.errors.map(e => e.message));
    }
    
    const courseErrors = validateCourseSpecificFields(data);
    errors.push(...courseErrors);
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🚀 Enhanced form submission started');
    
    // Prevent double submissions
    if (isLoading || isSubmitting) {
      console.log('⚠️ Submission already in progress');
      return;
    }

    // Check network connectivity
    if (!isOnline) {
      toast({
        title: "Ցանցային կապի խնդիր",
        description: "Ստուգեք ինտերնետ կապը և փորձեք կրկին",
        variant: "destructive"
      });
      return;
    }

    // Clear previous validation errors
    setValidationErrors([]);
    
    // Validate form data
    const errors = validateForm(formData);
    if (errors.length > 0) {
      setValidationErrors(errors);
      toast({
        title: "Տվյալների սխալ",
        description: errors[0], // Show first error
        variant: "destructive"
      });
      return;
    }
    
    setIsLoading(true);
    setIsSubmitting(true);
    setRetryAttempt(prev => prev + 1);

    try {
      console.log(`💾 Step 1: Saving to database (attempt ${retryAttempt})...`);
      const savedRegistration = await saveRegistrationToDatabase(formData);

      console.log('📧 Step 2: Sending confirmation email...');
      setIsSendingEmail(true);
      setEmailError(null);
      
      try {
        await sendRegistrationConfirmationEmail(formData, savedRegistration.id);
        setEmailSent(true);
        console.log('✅ Confirmation email sent successfully');
      } catch (emailError: any) {
        console.error('❌ Email sending failed:', emailError);
        setEmailError(emailError.message || 'Էլ. փոստը ուղարկելիս սխալ է տեղի ունեցել');
      } finally {
        setIsSendingEmail(false);
      }

      console.log('📱 Step 3: Sending Telegram notification...');
      try {
        await sendToTelegram(formData);
        console.log('✅ Telegram notification sent successfully');
      } catch (telegramError: any) {
        console.error("❌ Telegram notification failed:", telegramError);
        // Don't fail the whole process if Telegram fails
      }

      // Success feedback with enhanced message
      toast({
        title: "Հաջողություն! 🎉",
        description: "Դուք հաջողությամբ գրանցվեցիք դասընթացի համար։ Մենք շուտով կկապնվենք Ձեզ հետ:",
      });

      // Close the form dialog and show success dialog
      setIsFormDialogOpen(false);
      setShowSuccessDialog(true);

      // Reset form and states
      setFormData({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        course: prefilledCourse || '',
        faculty: prefilledFaculty || '',
        specialization: '',
        language: '',
        learningType: prefilledCourse === 'ai-teacher-training' ? 'group' : '' as any,
        learningFormat: prefilledCourse === 'ai-teacher-training' ? 'inPerson' : '' as any,
        duration: prefilledCourse === 'ai-teacher-training' ? '3' : ''
      });

      setEmailSent(false);
      setEmailError(null);
      setIsSendingEmail(false);
      setValidationErrors([]);
      setRetryAttempt(0);

    } catch (error: any) {
      console.error('❌ Enhanced registration error:', error);
      
      let errorMessage = "Գրանցման ժամանակ սխալ տեղացավ";
      let showRetry = false;
      
      // Enhanced error categorization
      if (error.type === RegistrationErrorType.NETWORK) {
        errorMessage = error.message;
        showRetry = true;
      } else if (error.type === RegistrationErrorType.PERMISSION) {
        errorMessage = error.message;
        showRetry = true;
      } else if (error.type === RegistrationErrorType.DUPLICATE) {
        errorMessage = error.message;
        showRetry = false;
      } else if (error.type === RegistrationErrorType.VALIDATION) {
        errorMessage = error.message;
        showRetry = false;
      } else {
        errorMessage = error.message || "Անհայտ սխալ տեղի ունեցավ";
        showRetry = error.retryable || false;
      }
      
      toast({
        title: "Սխալ",
        description: errorMessage + (showRetry ? " (կրկին փորձեք)" : ""),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => {
      const newData: RegistrationFormData = {
        ...prev,
        [field]: value
      };
      
      // Reset dependent fields when course changes
      if (field === 'course') {
        newData.specialization = '';
        newData.language = '';
        if (value === 'ai-teacher-training') {
          newData.learningType = 'group';
          newData.learningFormat = 'inPerson';
          newData.duration = '3';
        } else {
          newData.learningType = '' as any;
          newData.learningFormat = '' as any;
          newData.duration = '';
        }
      }
      
      // Reset language when specialization changes to frontend
      if (field === 'specialization' && value === 'frontend') {
        newData.language = '';
      }
      
      return newData;
    });
  };

  return (
    <>
      <Dialog open={isFormDialogOpen} onOpenChange={setIsFormDialogOpen}>
        <DialogTrigger asChild>
          {children}
        </DialogTrigger>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-2 border-border/70 bg-card text-card-foreground sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-center gap-2 text-center text-2xl font-bold text-primary">
              Դիմում ուղարկել
              {!isOnline && <WifiOff className="h-5 w-5 text-red-500" />}
              {isOnline && <Wifi className="h-5 w-5 text-primary" />}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {validationErrors.length > 0 && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3">
                <p className="text-sm font-medium text-destructive">Ուղղեք հետևյալ սխալները։</p>
                <ul className="mt-1 list-inside list-disc text-sm text-destructive">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <FormFields formData={formData} onInputChange={handleInputChange} />
            
            {!isOnline && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/15 p-3">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  ⚠️ Ցանցային կապ չկա: Ձեր տվյալները պահպանվելու են և ուղարկվելու, երբ կապը վերականգնվի:
                </p>
              </div>
            )}
            
            <Button 
              type="submit" 
              className="w-full rounded-lg bg-primary py-3 font-semibold text-primary-foreground transition-colors duration-300 hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50" 
              disabled={isLoading || isSubmitting || !isOnline}
            >
              {isLoading || isSubmitting ? (
                <span className="flex items-center">
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-primary-foreground"></div>
                  Ուղարկվում է... ({retryAttempt > 0 ? `փորձ ${retryAttempt}` : 'սպասեք'})
                </span>
              ) : !isOnline ? (
                "Ցանցային կապի սպասում"
              ) : (
                <>
                  Ուղարկել հայտը
                  <ChevronRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <SuccessDialog 
        open={showSuccessDialog} 
        onOpenChange={setShowSuccessDialog}
        emailSent={emailSent}
        emailError={emailError}
        isSendingEmail={isSendingEmail}
        onRetryEmail={() => {
          if (formData.firstName && formData.lastName && formData.email) {
            setIsSendingEmail(true);
            setEmailError(null);
            
            sendRegistrationConfirmationEmail(formData, 'retry')
              .then(() => {
                setEmailSent(true);
                setEmailError(null);
              })
              .catch((error) => {
                setEmailError(error.message || 'Էլ. փոստը ուղարկելիս սխալ է տեղի ունեցել');
              })
              .finally(() => {
                setIsSendingEmail(false);
              });
          }
        }}
      />
    </>
  );
};
